import React, { useState, useEffect } from "react";
import * as api from "../api";
import { useToast } from "../components/Toast";
import Modal, { ModalActions } from "../components/Modal";
import { PlusIcon, TrashIcon, EditIcon, EyeIcon, LockIcon } from "../components/Icons";

const roleColors = { Admin: "#F56565", Edit: "#667EEA", View: "#48BB78" };
const roleIcons = { Admin: <LockIcon />, Edit: <EditIcon />, View: <EyeIcon /> };
const roleDescriptions = {
  Admin: "Full access to all features including user management, project creation, and data editing.",
  Edit: "Can view all data and edit input variables, project details, and mappings.",
  View: "Read-only access to all dashboards, reports, and financial statements.",
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", role: "View", password: "" });
  const [resetTarget, setResetTarget] = useState(null); // { id, name }
  const [newPassword, setNewPassword] = useState("");
  const toast = useToast();

  const load = () => {
    api.getUsers().then(setUsers).catch(() => toast("Failed to load users", "error")).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    try {
      const payload = { ...newUser };
      if (!payload.password) delete payload.password;
      const res = await api.createUser(payload);
      if (res?.initial_password) {
        toast(`User added — temporary password: ${res.initial_password}`, "info");
      } else {
        toast("User added");
      }
      setShowAdd(false);
      setNewUser({ name: "", email: "", role: "View", password: "" });
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast("Password must be at least 6 characters", "error");
      return;
    }
    try {
      await api.updateUser(resetTarget.id, { password: newPassword });
      toast(`Password reset for ${resetTarget.name}`);
      setResetTarget(null);
      setNewPassword("");
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const handleRoleChange = async (uid, role) => {
    try {
      await api.updateUser(uid, { role });
      toast("Role updated");
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const handleDelete = async (uid) => {
    try {
      await api.deleteUser(uid);
      toast("User removed");
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div className="fade-in">
      <div className="flex justify-between items-center mb-24">
        <p className="text-sm text-muted">Manage user access and permissions across the platform.</p>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
          <PlusIcon /> Add User
        </button>
      </div>

      <div className="card mb-24">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Permissions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div className="flex items-center gap-10">
                      <div style={{
                        width: 30, height: 30, borderRadius: "50%",
                        background: `${roleColors[u.role]}20`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 12, fontWeight: 600, color: roleColors[u.role],
                      }}>
                        {u.name.charAt(0)}
                      </div>
                      <span className="fw-500">{u.name}</span>
                    </div>
                  </td>
                  <td className="text-muted">{u.email}</td>
                  <td>
                    <span className="badge" style={{ background: `${roleColors[u.role]}15`, color: roleColors[u.role] }}>
                      {roleIcons[u.role]} <span style={{ marginLeft: 4 }}>{u.role}</span>
                    </span>
                  </td>
                  <td className="text-xs text-muted">{roleDescriptions[u.role]?.split(",")[0]}</td>
                  <td>
                    <div className="flex gap-6">
                      <select className="input select" style={{ width: "auto", fontSize: 11, padding: "4px 24px 4px 8px" }}
                        value={u.role} onChange={(e) => handleRoleChange(u.id, e.target.value)}>
                        <option value="Admin">Admin</option>
                        <option value="Edit">Edit</option>
                        <option value="View">View</option>
                      </select>
                      <button className="btn btn-secondary" style={{ padding: "4px 8px", fontSize: 11 }}
                        title="Reset Password" onClick={() => { setResetTarget(u); setNewPassword(""); }}>
                        <LockIcon />
                      </button>
                      <button className="btn btn-danger" style={{ padding: "4px 8px" }} onClick={() => handleDelete(u.id)}>
                        <TrashIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Role Legend */}
      <div className="grid-3">
        {Object.entries(roleDescriptions).map(([role, desc]) => (
          <div key={role} className="card section-border-left" style={{ borderColor: roleColors[role] }}>
            <h4 className="fw-600 mb-8" style={{ fontSize: 14, color: roleColors[role] }}>{role}</h4>
            <p className="text-xs text-muted" style={{ lineHeight: 1.6 }}>{desc}</p>
          </div>
        ))}
      </div>

      {resetTarget && (
        <Modal title={`Reset Password — ${resetTarget.name}`} onClose={() => setResetTarget(null)}>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input className="input" type="password" value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min. 6 characters" autoFocus />
          </div>
          <ModalActions onCancel={() => setResetTarget(null)} onConfirm={handleResetPassword}
            confirmLabel="Reset Password" disabled={!newPassword} />
        </Modal>
      )}

      {showAdd && (
        <Modal title="Add New User" onClose={() => setShowAdd(false)}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input className="input" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="Enter name" />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="input" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="user@company.com" />
          </div>
          <div className="form-group">
            <label className="form-label">Role</label>
            <select className="input select" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
              <option value="Admin">Admin</option>
              <option value="Edit">Edit</option>
              <option value="View">View</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Password <span className="text-muted" style={{ fontWeight: 400 }}>(leave blank to use default: changeme123)</span></label>
            <input className="input" type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Set initial password" />
          </div>
          <ModalActions onCancel={() => setShowAdd(false)} onConfirm={handleAdd} confirmLabel="Add User" disabled={!newUser.name || !newUser.email} />
        </Modal>
      )}
    </div>
  );
}
